import { useCallback, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import { listReturns, resolveDispute, type TallyReturn } from './api'
import { ReturnStatusChip, formLabel, seatLabel } from './vocab'

/**
 * Returns somebody has challenged.
 *
 * ## A disputed return does not count, and the reason is kept forever
 *
 * Settling a dispute puts the return back on the ladder at *entered* — it has to be verified
 * again — and **the reason stays on the row**. A dispute that happened is a fact about that
 * return for good; clearing it on settlement would erase the only record that it was ever
 * challenged, which is precisely what somebody would later ask about.
 */
export function DisputesPage() {
  const { electionId } = useElectionContext()
  const [rows, setRows] = useState<TallyReturn[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!electionId) { setRows([]); return }
    try {
      setError(null)
      setRows(await listReturns({ electionId, status: 'DISPUTED', size: 200 }))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [electionId])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="A disputes list" />

  const settle = async (id: string) => {
    setBusyId(id)
    setError(null)
    try {
      const note = window.prompt('How was this settled? Recorded against the return.')
      if (note === null) { setBusyId(null); return }
      await resolveDispute(id, note)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-erp-base font-semibold">Disputes</h2>
        <p className="text-erp-xs text-muted-foreground mt-0.5">
          Returns somebody has challenged. None of these counts towards any board while the
          challenge stands.
        </p>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      {rows !== null && rows.length === 0 && !error && (
        <EmptyState
          title="Nothing is under challenge"
          hint="A return is disputed when an agent's copy differs from the filed figures, or
                when the paper itself is questioned. The reason is recorded and kept." />
      )}

      {(rows === null || rows.length > 0) && (
        <ErpTable head={(
          <>
            <Th>Place</Th>
            <Th>Race</Th>
            <Th>Status</Th>
            <Th className="text-right">Valid</Th>
            <Th>Why it is disputed</Th>
            <Th />
          </>
        )}>
          {rows === null ? <LoadingRows colSpan={6} />
            : rows.map((r) => (
              <tr key={r.id}>
                <Td>{r.unitName ?? '—'}</Td>
                <Td>
                  {seatLabel(r.seatTypeCode)}
                  <span className="block text-erp-xs text-muted-foreground">
                    {formLabel(r.formTypeCode)} · version {r.versionNo}
                  </span>
                </Td>
                <Td><ReturnStatusChip code={r.statusCode} /></Td>
                <Td className="text-right tabular-nums">{fmtNumber(r.validVotes)}</Td>
                <Td className="text-erp-xs">{r.disputeReason ?? '—'}</Td>
                <Td>
                  <button type="button" disabled={busyId !== null}
                    onClick={() => void settle(r.id)}
                    className="inline-flex items-center gap-1.5 text-erp-xs text-chocolate hover:underline">
                    {busyId === r.id && <Loader2 className="h-3 w-3 animate-spin" />}
                    Settle
                  </button>
                </Td>
              </tr>
            ))}
        </ErpTable>
      )}

      <Note tone="info">
        Settling returns the figures to <strong>entered</strong> — they need verifying again
        before counting. The reason for the dispute stays on the return permanently.
      </Note>
    </div>
  )
}
